
import React, { useState, useEffect, useMemo } from 'react';
import { DragDropContext, OnDragEndResponder } from '@hello-pangea/dnd';
import { useOptimizedLeads, useLeadsCount } from '@/hooks/useOptimizedLeads';
import { useUpdateLeadStatus } from '@/hooks/useLeads';
import { Lead, LeadStatus, LEAD_PIPELINE_STAGES, leadStatusLabels } from './types';
import { LeadPipelineColumn } from './LeadPipelineColumn';
import { LoadingState } from '@/components/error-handling/LoadingState';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { toast } from 'sonner';
import { useMemoryCleanup } from "@/utils/memory/memoryManager";

const LeadDndPipeline: React.FC = () => {
  const { data: fetchedLeadsData, isLoading, error: fetchError } = useOptimizedLeads({
    pageSize: 100 // Load more for pipeline view but still limit
  });
  const updateLeadStatusMutation = useUpdateLeadStatus();
  
  const [localLeads, setLocalLeads] = useState<Lead[]>([]);

  // Memory cleanup for component
  useMemoryCleanup(() => {
    setLocalLeads([]);
  });

  useEffect(() => {
    if (fetchedLeadsData) {
      setLocalLeads(fetchedLeadsData);
    }
  }, [fetchedLeadsData]);

  const handleDragEnd: OnDragEndResponder = (result) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;

    const leadId = draggableId;
    const newStatus = destination.droppableId as LeadStatus;
    const oldStatus = source.droppableId as LeadStatus;

    if (newStatus === oldStatus && destination.index === source.index) {
      return;
    }

    const movedLead = localLeads.find(l => l.id === leadId);
    if (!movedLead) {
      toast.error("Error finding lead data for UI update.");
      return;
    }

    // Optimistic UI update
    const optimisticallyUpdatedLeads = localLeads.map(l =>
      l.id === leadId ? { ...l, status: newStatus } : l
    );
    setLocalLeads(optimisticallyUpdatedLeads);

    updateLeadStatusMutation.mutate(
      { leadId, status: newStatus },
      {
        onSuccess: () => {
          const stageLabel = leadStatusLabels[newStatus] || newStatus;
          toast.success(`${movedLead.name || 'Lead'} moved to ${stageLabel}.`);
          // Data will be refetched by useOptimizedLeads query invalidation
        },
        onError: (error: any) => {
          toast.error(`Failed to move ${movedLead.name || 'Lead'}: ${error.message || 'Unknown error'}. Reverting.`);
          // Revert optimistic update
          setLocalLeads(prevLeads => prevLeads.map(l => 
            l.id === leadId ? { ...l, status: oldStatus } : l
          ));
        },
      }
    );
  };

  const groupedLeads = useMemo(() => {
    return localLeads.reduce((acc, lead) => {
      const statusKey = lead.status || 'new'; // Default to 'new' if status is somehow undefined
      if (!acc[statusKey]) {
        acc[statusKey] = [];
      }
      acc[statusKey].push(lead);
      return acc;
    }, {} as Record<LeadStatus, Lead[]>);
  }, [localLeads]);

  if (isLoading) {
    return <LoadingState message="Loading leads pipeline..." />;
  }

  if (fetchError) {
    return (
      <Alert variant="destructive" className="mt-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Error Loading Leads</AlertTitle>
        <AlertDescription>{fetchError.message || "An unknown error occurred."}</AlertDescription>
      </Alert>
    );
  }
  
  if (!fetchedLeadsData && !isLoading) {
    return <p className="text-center text-muted-foreground mt-8">No leads found or failed to load.</p>;
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex space-x-3 overflow-x-auto pb-4 -mx-2 px-2">
        {LEAD_PIPELINE_STAGES.map(stage => (
          <LeadPipelineColumn
            key={stage.id}
            stage={stage}
            leads={groupedLeads[stage.id] || []}
          />
        ))}
      </div>
    </DragDropContext>
  );
};

export default LeadDndPipeline;
